"use client"
import { useState, useEffect, useRef } from "react"
import { Clock, CheckCircle2, AlertCircle, Pause, Play, XCircle, Send } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Checkbox } from "../../components/ui/checkbox"
import { getTempToken } from "../../api/authService"
import { Input } from "../../components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"

interface EmailTemplate {
  id: number
  title: string
  description: string
}

interface RecipientGroup {
  id: string
  label: string
  recipients: Array<{ email: string; name: string }>
}

interface BatchProgress {
  state: "running" | "paused" | "cancelled" | "completed"
  total: number
  sent: number
  remaining: number
  elapsed_seconds: number
  eta_seconds: number
}

export function TemplateEmailSender({ allUsers, adminUsers, portals, bioDataList }: any) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string>("")
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [customRecipients, setCustomRecipients] = useState("")
  const [customSubject, setCustomSubject] = useState("")
  const [sending, setSending] = useState(false)
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null)
  const [activeBatchId, setActiveBatchId] = useState<number | null>(null)
  const pollingRef = useRef<number | null>(null)
  const [showConfirmCancel, setShowConfirmCancel] = useState(false)

  const recipientGroups: RecipientGroup[] = [
    {
      id: "all-users",
      label: "All Users",
      recipients: allUsers.map((user: any) => ({
        email: user.email,
        name:
          user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : user.name || "Unnamed",
      })),
    },
    ...Array.from(new Set(allUsers.map((u: any) => u.roles?.[0]))).map((role: any) => ({
      id: role,
      label: role ? role.charAt(0).toUpperCase() + role.slice(1) : "Unknown",
      recipients: allUsers
        .filter((u: any) => u.roles?.[0] === role)
        .map((u: any) => ({
          email: u.email,
          name: u.first_name || u.last_name ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : u.name || "Unnamed",
        })),
    })),
    {
      id: "bio-data-list",
      label: "Bio Data List",
      recipients: bioDataList.map((user: any) => ({
        email: user.email,
        name:
          user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : user.name || "Unnamed",
      })),
    },
    ...portals.map((p: any) => ({
      id: p.portal,
      label: `${p.portal.charAt(0).toUpperCase() + p.portal.slice(1)} Registrants`,
      recipients: p.data.map((item: any) => ({
        email: item.email_address,
        name:
          item.first_name || item.surname
            ? `${item.first_name || ""} ${item.surname || ""}`.trim()
            : item.full_name || "Unnamed",
      })),
    })),
  ]

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/custom-api/v1/email-templates", {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setTemplates(data)
      }
    } catch (error) {
      console.error("[v0] Error fetching templates:", error)
    }
  }

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]))
  }

  const selectedRecipients = selectedGroups.flatMap((groupId) => {
    const group = recipientGroups.find((g) => g.id === groupId)
    return group?.recipients || []
  })

  const dedupedRecipients = Array.from(new Map(selectedRecipients.map((r) => [r.email, r])).values())

  const customEmails = customRecipients
    .split(",")
    .map((e) => e.trim())
    .filter((e) => e)
    .map((email) => ({ email, name: "Custom Recipient" }))

  const finalRecipients = Array.from(new Map([...dedupedRecipients, ...customEmails].map((r) => [r.email, r])).values())

  const handleSendTemplateEmail = async () => {
    if (!selectedTemplate) {
      alert("Please select a template")
      return
    }

    if (finalRecipients.length === 0) {
      alert("Please select recipients")
      return
    }

    setSending(true)
    setBatchProgress({
      state: "running",
      total: finalRecipients.length,
      sent: 0,
      remaining: finalRecipients.length,
      elapsed_seconds: 0,
      eta_seconds: 0,
    })

    try {
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/custom-api/v1/queue-template-emails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          template_id: Number.parseInt(selectedTemplate),
          recipients: finalRecipients,
          subject: customSubject || undefined,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        setActiveBatchId(data.batch_id)
        startPolling(data.batch_id)
      } else {
        alert("Failed to queue emails")
        setSending(false)
        setBatchProgress(null)
      }
    } catch (error) {
      console.error("[v0] Error sending template emails:", error)
      alert("Error sending emails")
      setSending(false)
      setBatchProgress(null)
    }
  }

  const fetchBatchProgress = async (batchId: number) => {
    const token = await getTempToken()
    try {
      const res = await fetch(`https://tsoministries.com/wp-json/custom-api/v1/email-batch/${batchId}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setBatchProgress(data)
        if (["completed", "cancelled"].includes(data.state)) {
          stopPolling()
          setSending(false)
        }
      }
    } catch (err) {
      console.error("[v0] Error fetching batch progress:", err)
    }
  }

  const startPolling = (batchId: number) => {
    if (pollingRef.current !== null) clearInterval(pollingRef.current)
    fetchBatchProgress(batchId)
    pollingRef.current = window.setInterval(() => fetchBatchProgress(batchId), 1000)
  }

  const stopPolling = () => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  const updateBatchState = async (batchId: number, state: string) => {
    try {
      const token = await getTempToken()
      await fetch(`https://tsoministries.com/wp-json/custom-api/v1/email-batch/${batchId}/state`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ state }),
      })
      fetchBatchProgress(batchId)
    } catch (error) {
      console.error("[v0] Error updating batch state:", error)
    }
  }

  if (batchProgress) {
    const progressPercent = (batchProgress.sent / batchProgress.total) * 100
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}m ${secs}s`
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Sending Emails
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {batchProgress.sent} of {batchProgress.total} emails sent
              </span>
              <span className="text-sm text-gray-500">{Math.round(progressPercent)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-500">Elapsed</p>
              <p className="text-lg font-semibold">{formatTime(batchProgress.elapsed_seconds)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Remaining</p>
              <p className="text-lg font-semibold">{batchProgress.remaining}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">ETA</p>
              <p className="text-lg font-semibold">{formatTime(batchProgress.eta_seconds)}</p>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            {batchProgress.state === "running" && (
              <Button onClick={() => updateBatchState(activeBatchId!, "paused")} variant="outline" className="gap-2">
                <Pause className="h-4 w-4" />
                Pause
              </Button>
            )}
            {batchProgress.state === "paused" && (
              <Button onClick={() => updateBatchState(activeBatchId!, "running")} className="gap-2">
                <Play className="h-4 w-4" />
                Resume
              </Button>
            )}
            {["running", "paused"].includes(batchProgress.state) && (
              <Button onClick={() => setShowConfirmCancel(true)} variant="destructive" className="gap-2">
                <XCircle className="h-4 w-4" />
                Cancel
              </Button>
            )}

            {batchProgress.state === "completed" && (
              <Button disabled className="gap-2 w-full">
                <CheckCircle2 className="h-4 w-4" />
                All emails sent!
              </Button>
            )}

            {batchProgress.state === "cancelled" && (
              <Button disabled className="gap-2 w-full bg-transparent" variant="outline">
                <AlertCircle className="h-4 w-4" />
                Sending cancelled
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">Send Template Email</CardTitle>
        <CardDescription>Select a template and recipients to send emails</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Template Selection */}
        <div>
          <label className="text-sm font-medium">Select Template</label>
          <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Choose a template..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id.toString()}>
                  {t.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Custom Subject Override */}
        <div>
          <label className="text-sm font-medium">Subject Line (Optional)</label>
          <Input
            placeholder="Leave blank to use template subject"
            value={customSubject}
            onChange={(e) => setCustomSubject(e.target.value)}
            className="mt-1"
          />
        </div>

        {/* Recipient Selection */}
        <div>
          <label className="text-sm font-medium mb-3 block">Select Recipients</label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {recipientGroups.map((group) => (
              <label key={group.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                <Checkbox
                  checked={selectedGroups.includes(group.id)}
                  onCheckedChange={() => handleGroupToggle(group.id)}
                />
                <span className="text-sm">
                  {group.label} ({group.recipients.length})
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Custom Recipients */}
        <div>
          <label className="text-sm font-medium">Add Custom Recipients</label>
          <Input
            placeholder="email1@example.com, email2@example.com"
            value={customRecipients}
            onChange={(e) => setCustomRecipients(e.target.value)}
            className="mt-1"
          />
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm">
            <strong>{finalRecipients.length} recipients</strong> will receive this email
          </p>
        </div>

        <Button
          onClick={handleSendTemplateEmail}
          disabled={sending || !selectedTemplate || finalRecipients.length === 0}
          className="w-full gap-2"
          size="lg"
        >
          <Send className="h-4 w-4" />
          Send Template Email
        </Button>
      </CardContent>
    </Card>
  )
}
